import React from 'react';
import PropTypes from 'prop-types';
import {NotificationType, showNotification} from 'qwc2/actions/windows';
import {getStore} from 'qwc2/stores/StandardStore';
import LocaleUtils from 'qwc2/utils/LocaleUtils';
import '../style/ErrorBoundary.css';

// The translations are not available if the application failed before the locale was loaded
export function safeTr(key, fallback, ...args) {
    try {
        const text = LocaleUtils.tr(key, ...args);
        return text === key ? fallback : text;
    } catch (error) {
        return fallback;
    }
}

/**
 * Shows an error notification, one per name. A sticky notification stays until it is closed.
 */
export function notifyError(name, text, sticky = false) {
    try {
        getStore()?.dispatch(showNotification("errorboundary:" + name, text, NotificationType.ERROR, sticky));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log("Could not show error notification", error);
    }
}

function sameKeys(a = [], b = []) {
    return a.length === b.length && a.every((value, idx) => value === b[idx]);
}

/**
 * Keeps an error in the rendering of its children local: instead of unmounting the whole application
 * (white page), the `fallback` is shown in place of the children.
 *
 * - `fallback`: element, or function `(error, retry) => element`. Nothing is rendered if omitted.
 * - `resetKeys`: the boundary tries to render its children again when one of these values changes.
 * - `onError`: called with `(error, info)` once an error was caught.
 * - `name`: used for the log output.
 */
export default class ErrorBoundary extends React.Component {
    static propTypes = {
        children: PropTypes.node,
        fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
        name: PropTypes.string,
        onError: PropTypes.func,
        resetKeys: PropTypes.array
    };
    static defaultProps = {
        name: "unnamed",
        resetKeys: []
    };
    static getDerivedStateFromError(error) {
        return {error: error ?? new Error("Unknown error")};
    }
    state = {
        error: null
    };
    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("[ErrorBoundary] " + this.props.name, error, info?.componentStack);
        this.props.onError?.(error, info);
    }
    componentDidUpdate(prevProps) {
        if (this.state.error && !sameKeys(prevProps.resetKeys, this.props.resetKeys)) {
            this.retry();
        }
    }
    retry = () => {
        this.setState({error: null});
    };
    render() {
        if (this.state.error) {
            const {fallback} = this.props;
            return typeof fallback === 'function' ? fallback(this.state.error, this.retry) : (fallback ?? null);
        }
        return this.props.children;
    }
}

/**
 * Inline fallback of a part of a window (i.e. a tab): short message and a button to try again.
 */
export function inlineErrorFallback(error, retry) {
    return (
        <div className="errorboundary-box">
            <div className="errorboundary-title">{safeTr("errorboundary.title", "Dieser Bereich konnte nicht angezeigt werden.")}</div>
            <div className="errorboundary-message">{String(error?.message ?? error)}</div>
            <button className="button" onClick={retry} type="button">{safeTr("errorboundary.retry", "Erneut versuchen")}</button>
        </div>
    );
}

/**
 * Wraps a (plugin) component into an error boundary. A failing plugin disappears and an error
 * notification is shown, the rest of the application keeps working.
 * The statics qwc2 reads from plugins (WrappedComponent, availableIn3D, defaultProps) are passed through.
 */
export function withErrorBoundary(Component, {name = null, notify = true} = {}) {
    const inner = Component.WrappedComponent ?? Component;
    const pluginName = name ?? inner.displayName ?? inner.name ?? "Plugin";
    const Wrapped = (props) => {
        const onError = notify ? () => {
            notifyError(pluginName, safeTr("errorboundary.pluginfailed", pluginName + " ist ausgefallen. Der Rest der Anwendung ist weiter nutzbar.", pluginName), true);
        } : undefined;
        return (
            <ErrorBoundary fallback={null} name={pluginName} onError={onError}>
                <Component {...props} />
            </ErrorBoundary>
        );
    };
    Wrapped.displayName = "WithErrorBoundary(" + pluginName + ")";
    Wrapped.WrappedComponent = inner;
    Wrapped.availableIn3D = inner.availableIn3D;
    return Wrapped;
}
